import React, { useMemo } from 'react';
import { EdgeProps, BaseEdge, Position, useStore, ReactFlowState } from 'reactflow';
import { Point, Rect, routeEdge, roundedOrthogonalPath } from './routing';
import { isEdgeRoutingEnabled } from './edgeRoutingFlag';
import { legacyEdgePath } from '../../../edit/legacyEdgePath';

// Unified obstacle-avoiding edge (issue #918). One renderer for every edge type:
// direction is derived live from geometry, and `edge.type` is preserved untouched by
// registering each type key to this component. `error-loopback` renders in red via the
// `error` prop; the value still round-trips in the edge data for the backend scheduler.
//
// A runtime flag can disable routing (see edgeRoutingFlag); disabled edges fall back to a
// plain smoothstep, or a loop arc for backward edges — cheap and non-routed.

const dirFor = (pos?: Position): Point => {
  switch (pos) {
    case Position.Left:
      return { x: -1, y: 0 };
    case Position.Top:
      return { x: 0, y: -1 };
    case Position.Bottom:
      return { x: 0, y: 1 };
    case Position.Right:
    default:
      return { x: 1, y: 0 };
  }
};

// Node rectangles to route around: every rendered node with a measured size, minus the
// ancestor group boxes that contain either endpoint (a subflow container must not block
// its own children's edges). The endpoints' own leaf boxes stay in — the router pins the
// route to the handles and routes around everything else.
const buildObstacles = (internals: ReactFlowState['nodeInternals'], sourceId: string, targetId: string): Rect[] => {
  const ancestorsOf = (id: string): string[] => {
    const chain: string[] = [];
    let node = internals.get(id);
    while (node?.parentNode) {
      chain.push(node.parentNode);
      node = internals.get(node.parentNode);
    }
    return chain;
  };
  const skip = new Set([...ancestorsOf(sourceId), ...ancestorsOf(targetId)]);
  const rects: Rect[] = [];
  internals.forEach((node) => {
    if (skip.has(node.id) || node.hidden) return;
    const pos = node.positionAbsolute;
    if (!pos || !node.width || !node.height) return;
    rects.push({ x: pos.x, y: pos.y, width: node.width, height: node.height });
  });
  return rects;
};

// Legacy (routing-disabled) path uses legacyEdgePath (see legacyEdgePath.test.ts).

const RoutedEdge: React.FC<EdgeProps & { error?: boolean }> = ({
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  style,
  markerEnd,
  error = false,
}) => {
  const routingEnabled = isEdgeRoutingEnabled();
  const nodeInternals = useStore((s: ReactFlowState) => s.nodeInternals);
  const isSelfLoop = source === target;

  // A defined waypoint set opts the edge out of live routing (`[]` = deliberately
  // straight). `undefined` → compute around obstacles now.
  const stored: Point[] | undefined = Array.isArray(data?.waypoints) ? data.waypoints : undefined;

  const waypoints = useMemo(() => {
    if (!routingEnabled || isSelfLoop) return [];
    if (stored) return stored;
    const obstacles = buildObstacles(nodeInternals, source, target);
    return routeEdge({
      source: { x: sourceX, y: sourceY },
      target: { x: targetX, y: targetY },
      sourceDir: dirFor(sourcePosition),
      targetDir: dirFor(targetPosition),
      obstacles,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routingEnabled, isSelfLoop, stored, nodeInternals, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition]);

  let path: string;
  let mid: Point;
  if (!routingEnabled || isSelfLoop) {
    ({ path, mid } = legacyEdgePath(sourceX, sourceY, targetX, targetY, isSelfLoop));
  } else {
    const full = [{ x: sourceX, y: sourceY }, ...waypoints, { x: targetX, y: targetY }];
    path = roundedOrthogonalPath(full);
    mid = full[Math.floor(full.length / 2)];
  }

  const edgeStyle = error ? { stroke: '#ef4444', strokeWidth: 2, strokeDasharray: '6 4', ...style } : style;

  return (
    <>
      <BaseEdge path={path} style={edgeStyle} markerEnd={markerEnd} />
      {error && (
        <g pointerEvents="none">
          <circle cx={mid.x} cy={mid.y} r={10} fill="#ef4444" />
          <text x={mid.x} y={mid.y + 4} textAnchor="middle" fontSize={10} fill="#ffffff" fontWeight={700}>
            !
          </text>
        </g>
      )}
    </>
  );
};

export const ErrorRoutedEdge: React.FC<EdgeProps> = (props) => <RoutedEdge {...props} error />;

export default RoutedEdge;
